import React from "react";
import { HiX } from "react-icons/hi";
import { twMerge } from "tailwind-merge";

const TONES = {
  info: "border-blue-200 bg-blue-50 text-blue-800",
  warning: "border-amber-200 bg-amber-50 text-amber-800",
  failure: "border-red-200 bg-red-50 text-red-800",
  success: "border-green-200 bg-green-50 text-green-800",
  gray: "border-gray-200 bg-gray-50 text-gray-700",
} as const;

const DISMISS_TONES = {
  info: "bg-blue-100 text-blue-500 hover:bg-blue-200 focus:ring-blue-400",
  warning:
    "bg-amber-100 text-amber-500 hover:bg-amber-200 focus:ring-amber-400",
  failure: "bg-red-100 text-red-500 hover:bg-red-200 focus:ring-red-400",
  success:
    "bg-green-100 text-green-500 hover:bg-green-200 focus:ring-green-400",
  gray: "bg-gray-100 text-gray-500 hover:bg-gray-200 focus:ring-gray-400",
} as const;

export type AlertTone = keyof typeof TONES;

export interface AlertMessageProps {
  color?: AlertTone;
  icon?: React.ComponentType<{ className?: string }>;
  onDismiss?: () => void;
  dismissLabel?: string;
  className?: string;
  children?: React.ReactNode;
}

export const AlertMessage: React.FC<AlertMessageProps> = ({
  color = "info",
  icon: Icon,
  onDismiss,
  dismissLabel = "Dismiss",
  className,
  children,
}) => (
  <div
    role="alert"
    className={twMerge(
      "flex flex-col gap-2 rounded-lg border p-4 text-sm",
      TONES[color],
      className
    )}
  >
    <div className="flex items-center">
      {Icon && <Icon className="mr-3 inline h-5 w-5 flex-shrink-0" />}
      <div>{children}</div>
      {onDismiss && (
        <button
          type="button"
          aria-label={dismissLabel}
          onClick={onDismiss}
          className={twMerge(
            "-m-1.5 ml-auto inline-flex h-8 w-8 rounded-lg p-1.5 focus:ring-2",
            DISMISS_TONES[color]
          )}
        >
          <HiX aria-hidden className="h-5 w-5" />
        </button>
      )}
    </div>
  </div>
);

export default AlertMessage;
